import React, {
  useState,
  useEffect,
  useMemo,
  useContext,
  useCallback,
  useRef,
} from 'react';
import { ThemeContext } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { subDays, addDays, format } from 'date-fns';
import pt from 'date-fns/locale/pt';
import { Form } from '@unform/web';
import { Pie, Bar } from 'react-chartjs-2';
import ProgressBar from '@ramonak/react-progress-bar';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

import { Role } from '../../utils/variables';
import { updateCurrentDate } from 'store/modules/usuario/actions';
import api from 'services/api';
import { show } from 'store/modules/dialog/actions';

import Input from 'components/Input';
import Widget from 'components/Widget';
import SelectDate from 'components/SelectDate';
import MessageBox from 'components/MessageBox';
import PieChartLoader from 'components/ContentLoader/PieChartLoader';
import FilaDownloadLoader from 'components/ContentLoader/FilaDownloadLoader';
import NotFound from 'components/NotFound';
import Flag from 'components/Flag';
import DropDownMenu, { Action } from 'components/DropDownMenu';
import Switch from 'components/Switch/withoutForm';
import Tooltip from 'components/Tooltip';
import Button from 'components/Button';

import Download from 'assets/download.svg';
import { graphSemanalOptions, PlanilhaDiariaConfig } from './ChartConfig';

import {
  FiVideo,
  FiVideoOff,
  FiRefreshCcw,
  FiInfo,
  FiUploadCloud,
  FiSearch,
  FiActivity,
  FiFilm,
  FiBookmark,
  FiThumbsDown,
  FiCheckCircle,
  FiTv,
} from 'react-icons/fi';

import {
  Container,
  Content,
  Header,
  LeftContent,
  RightContent,
  Card,
  ChartContent,
  PieChartContent,
  Table,
  Scroll,
  TdFlag,
} from './styles';
import { Comercial, Emissora } from 'pages/@types';
import AppError from 'utils/AppError';

interface GraphData {
  options?: any;
  data: { datasets: any; labels?: any };
}

export default function Dashboard() {
  // react-router-dom
  const navigate = useNavigate();

  const { emissora, currentDate, profile } = useSelector(
    (state: any) => state.usuario,
  );
  const dispatch = useDispatch();

  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );

  const [loadingWidget, setLoadingWidget] = useState(false);
  const [widgets, setWidgets] = useState({
    comerciaisGravados: 0,
    comerciaisNaoGravados: 0,
    comerciaisGravadosArquivados: 0,
    comerciaisNaoGravadosArquivados: 0,
    semPlanilha: 0,
    globoExpress: 0,
    midiasAvulsasGravadas: 0,
    midiasAvulsas: 0,
    comerciaisReprovados: 0,
    chamadasAprovadas: 0,
    chamadasValidas: 0,
    autorizadosSemPlanilha: 0,
  });
  const [planilhaDiaria, setPlanilhaDiaria] = useState<GraphData>({
    data: { datasets: [] },
  });
  const [graphSemanal, setGraphSemanal] = useState<GraphData>({
    data: { datasets: [] },
  });
  // const [filaDownloads, setFilaDownloads] = useState<Comercial[]>([]);
  const [filtro, setFiltro] = useState('');
  const [filaDownloadsCompleta, setFilaDownloadsCompleta] = useState<
    Comercial[]
  >([]);
  const [selectedFila, setSelectedFila] = useState<Comercial>({} as Comercial);
  const [uploadProgress, setUploadProgress] = useState(0);

  const filaDownloads = filaDownloadsCompleta.filter(
    f =>
      f.titulo.includes(filtro.toUpperCase()) ||
      f.codmaterial.toString().includes(filtro),
  );
  // ordem da fila de download
  const orderStatusFilaDownload = [
    'erro',
    'emandamento',
    'pendente',
    'reenvio',
    'indisponivel',
  ];

  const inputFileRef = useRef<HTMLInputElement>(null);

  function processUpload(e: any) {
    // Atualiza Fila Downloads para mostrar progresso do Upload
    const fileUploading = filaDownloadsCompleta.filter(c => c.uploading);

    if (fileUploading.length === 0) {
      setFilaDownloadsCompleta(prevState =>
        prevState.map(c =>
          c.id !== selectedFila.id ? c : { ...c, uploading: true },
        ),
      );
      const file = e.target.files[0];
      if (file) {
        const data = new FormData();
        data.append('file', file);
        data.append('comercial_id', selectedFila.id.toString());
        api
          .post(`/midias`, data, {
            timeout: 180000 * 3,
            onUploadProgress: e => {
              const progress = e.total
                ? Math.round((e.loaded * 100) / e.total)
                : 0;
              setUploadProgress(progress);
            },
          })
          .then(() => {
            setTimeout(() => 1000);
            setUploadProgress(0);
            toast.success('Mídia carregada com sucesso');
            // Comercial com mídia não fica mais na fila de download
            // setFilaDownloads(
            //   filaDownloads.filter(c => c.id !== selectedFila.id),
            // );
            setFilaDownloadsCompleta(prevState =>
              prevState.filter(c => c.id !== selectedFila.id),
            );
          })
          .catch(err => {
            setTimeout(() => 1000);
            // Upload finalizado, volta a Flag
            setFilaDownloadsCompleta(prevState =>
              prevState.map(c =>
                c.id !== selectedFila.id ? c : { ...c, uploading: false },
              ),
            );
            if (err.status === 406) {
              const optionBase = {
                type: 'error',
                text: err.message,
              };
              dispatch(show(optionBase));
            }
            // setLoadingSubmit(false)
          })
          .finally(() => {
            if (inputFileRef.current) inputFileRef.current.value = '';
          });
      }
    } else {
      toast.error('É permitido apenas um upload');
    }
  }

  // pega o tema para enviar pro chart
  const themeContext = useContext(ThemeContext);

  const dateFormatted = useMemo(() => {
    if (currentDate) {
      return format(new Date(currentDate), "d 'de' MMMM", { locale: pt });
    } else return format(new Date(), "d 'de' MMMM", { locale: pt });
  }, [currentDate]);

  const handlePrevDay = useCallback(() => {
    dispatch(updateCurrentDate(subDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);

  const handleNextDay = useCallback(() => {
    dispatch(updateCurrentDate(addDays(new Date(currentDate), 1)));
  }, [currentDate, dispatch]);
  const handleDateChange = useCallback(
    date => {
      date && dispatch(updateCurrentDate(date));
    },
    [dispatch],
  );

  const handleBarGraphClick = useCallback(
    elements => {
      if (elements.length > 0) {
        if (elements[0]._datasetIndex === 0) {
          navigate('/dashboard/comerciaissemplanilha');
        } else {
          if (elements[0]._index !== 6)
            dispatch(
              updateCurrentDate(
                subDays(new Date(currentDate), 6 - elements[0]._index),
              ),
            );
          if (elements[0]._datasetIndex === 1)
            navigate('/dashboard/comerciaisnaogravados');
          else navigate('/dashboard/comerciaisgravados');
        }
      }
    },
    [currentDate, dispatch],
  );

  const handleGoToComerciaisIndisponiveis = useCallback(() => {
    navigate('/dashboard/comerciaisindisponiveis');
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    async function loadDashboard() {
      setLoadingWidget(true);
      try {
        const response = await api.get('/dashboard', {
          signal: controller.signal,

          params: {
            date: format(new Date(currentDate), 'yyyy-MM-dd'),
            emissora_id: emissora.value,
          },
        });
        if (response && response.data) {
          setWidgets(response.data.widgets);
          // setFilaDownloads(response.data.filaDownloads);
          setFilaDownloadsCompleta(response.data.filaDownloads);
          if (response.data.planilhadiaria) {
            setPlanilhaDiaria({
              options: PlanilhaDiariaConfig(themeContext),
              data: {
                labels: [
                  response.data.planilhadiaria.pendentes.label,
                  response.data.planilhadiaria.disponiveiseng.label,
                  response.data.planilhadiaria.gravados.label,
                  response.data.planilhadiaria.conferidos.label,
                ],
                datasets: [
                  {
                    label: 'Planilha diária',
                    pointRadius: 0,
                    pointHoverRadius: 0,
                    backgroundColor: [
                      themeContext?.graph.pendentes,
                      themeContext?.graph.disponiveiseng,
                      themeContext?.graph.gravados,
                      themeContext?.graph.conferidos,
                    ],
                    borderWidth: 0,
                    data: [
                      response.data.planilhadiaria.pendentes.value,
                      response.data.planilhadiaria.disponiveiseng.value,
                      response.data.planilhadiaria.gravados.value,
                      response.data.planilhadiaria.conferidos.value,
                    ],
                  },
                ],
              },
            });
          } else {
            setPlanilhaDiaria({ data: { datasets: [] } });
          }
          if (response.data.graphSemanal) {
            try {
              setGraphSemanal({
                options: graphSemanalOptions(themeContext).options,
                data: {
                  labels: [
                    response.data.graphSemanal[0].date,
                    response.data.graphSemanal[1].date,
                    response.data.graphSemanal[2].date,
                    response.data.graphSemanal[3].date,
                    response.data.graphSemanal[4].date,
                    response.data.graphSemanal[5].date,
                    response.data.graphSemanal[6].date,
                  ],
                  datasets: [
                    {
                      label: 'SEM PLANILHA',
                      backgroundColor: themeContext?.graph.semplanilha,
                      borderWidth: 0,
                      data: [
                        response.data.graphSemanal[0].semPlanilha,
                        response.data.graphSemanal[1].semPlanilha,
                        response.data.graphSemanal[2].semPlanilha,
                        response.data.graphSemanal[3].semPlanilha,
                        response.data.graphSemanal[4].semPlanilha,
                        response.data.graphSemanal[5].semPlanilha,
                        response.data.graphSemanal[6].semPlanilha,
                      ],
                    },
                    {
                      label: 'NÃO GRAVADOS',
                      backgroundColor: themeContext?.graph.naogravados,
                      borderWidth: 0,
                      data: [
                        response.data.graphSemanal[0].naoGravados,
                        response.data.graphSemanal[1].naoGravados,
                        response.data.graphSemanal[2].naoGravados,
                        response.data.graphSemanal[3].naoGravados,
                        response.data.graphSemanal[4].naoGravados,
                        response.data.graphSemanal[5].naoGravados,
                        response.data.graphSemanal[6].naoGravados,
                      ],
                    },
                    {
                      label: 'NÃO CONFERIDOS',
                      backgroundColor: themeContext?.graph.naoconferidos,
                      borderWidth: 0,
                      data: [
                        response.data.graphSemanal[0].naoConferidos,
                        response.data.graphSemanal[1].naoConferidos,
                        response.data.graphSemanal[2].naoConferidos,
                        response.data.graphSemanal[3].naoConferidos,
                        response.data.graphSemanal[4].naoConferidos,
                        response.data.graphSemanal[5].naoConferidos,
                        response.data.graphSemanal[6].naoConferidos,
                      ],
                    },
                  ],
                },
              });
            } catch (err) {
              console.log(err);
            }
          } else {
            setGraphSemanal({ data: { datasets: [] } });
          }
        }
      } catch (e) {
        if (e instanceof Error) {
          toast.error(e.message);
        } else {
          const appError = e as AppError;
          toast.error(appError.message);
        }
      } finally {
        setLoadingWidget(false);
      }
    }

    if (emissora) loadDashboard();
    return () => {
      controller.abort();
    };
  }, [currentDate, emissora, themeContext]);

  async function handleActionSelect(fila: Comercial, action: Action) {
    setSelectedFila(fila);
    const optionBase = {
      type: 'question',
      text: `Reiniciar download de ${fila.titulo} ?`,
      confirmButtonText: 'CONFIRMAR',
      callback: {
        url: `/resetdownload/${fila.id}`,
        method: api.put,
      },
      haveCallback: true,
      successMessage: 'Download reiniciado!',
      cancelButtonText: 'NÃO',
      showCancelButton: false,
      showConfirmButton: true,
      showCloseButton: true,
    };
    switch (action.value) {
      case 1:
        dispatch(show(optionBase));
        break;
      case 2:
        setSelectedFila(fila);
        if (inputFileRef && inputFileRef.current) inputFileRef.current.click();
        break;
      default:
        break;
    }
  }

  useEffect(() => {
    if (callbackDialog && callbackDialog.success)
      if (callbackDialog.success.action === 'success' && selectedFila) {
        // setFilaDownloads(prevState =>
        //   prevState.map(c =>
        //     c.id !== selectedFila.id
        //       ? { ...c }
        //       : { ...c, statusdownload: 'pendente' },
        //   ),
        // );
        setFilaDownloadsCompleta(prevState =>
          prevState.map(c =>
            c.id !== selectedFila.id ? c : { ...c, statusdownload: 'pendente' },
          ),
        );
      }
  }, [callbackDialog, selectedFila]);

  const handleSwitchChange = useCallback(
    async (e, fila) => {
      const globoexpress = e.target.checked;

      // setFilaDownloads(
      //   filaDownloads.map(c =>
      //     c.id === fila.id ? { ...c, loading: true } : { ...c },
      //   ),
      // );
      setFilaDownloadsCompleta(prevState =>
        prevState.map(c =>
          c.id === fila.id ? { ...c, loading: true } : { ...c },
        ),
      );

      try {
        const { data } = await api.put(`/globoexpress/${fila.id}`, {
          globoexpress,
        });
        setFilaDownloadsCompleta(prevState =>
          prevState
            .map(f =>
              f.id === fila.id
                ? {
                    ...f,
                    loading: false,

                    globoexpress: data.globoexpress,
                  }
                : f,
            )
            .sort((a, b) => {
              if (a.id < b.id) return 1;
              if (b.id < a.id) return -1;
              return 0;
            })
            .sort((a, b) => {
              return (
                orderStatusFilaDownload.indexOf(a.statusdownload) -
                orderStatusFilaDownload.indexOf(b.statusdownload)
              );
            })
            .sort((a, b) => {
              if (a.globoexpress < b.globoexpress) return 1;
              if (b.globoexpress < a.globoexpress) return -1;
              return 0;
            }),
        );
        // setFilaDownloads(
        //   filaDownloads
        //     .map(f =>
        //       f.id === fila.id
        //         ? {
        //             ...f,
        //             loading: false,

        //             globoexpress: data.globoexpress,
        //           }
        //         : f,
        //     )
        //     .sort((a, b) => {
        //       if (a.id < b.id) return 1;
        //       if (b.id < a.id) return -1;
        //       return 0;
        //     })
        //     .sort((a, b) => {
        //       return (
        //         orderStatusFilaDownload.indexOf(a.statusdownload) -
        //         orderStatusFilaDownload.indexOf(b.statusdownload)
        //       );
        //     })
        //     .sort((a, b) => {
        //       if (a.globoexpress < b.globoexpress) return 1;
        //       if (b.globoexpress < a.globoexpress) return -1;
        //       return 0;
        //     }),
        // );

        toast.success('Prioridade alterada');
      } catch (e) {
        if (e instanceof Error) toast.error(e.message);
        else if (e instanceof AppError) toast.error(e.message);
        else {
          const appError = e as AppError;
          if (appError.message) toast.error(appError.message);
          else toast.error('Erro inesperado');
        }
      }
    },
    [orderStatusFilaDownload],
  );

  // const handleFiltro = useCallback(
  //   e => {
  //     const filteredFila = filaDownloadsCompleta.filter(
  //       f =>
  //         f.titulo.includes(e.target.value.toUpperCase()) ||
  //         f.codmaterial.toString().includes(e.target.value),
  //     );
  //     setFilaDownloads(filteredFila);
  //   },
  //   [filaDownloadsCompleta],
  // );

  return (
    <Container>
      <SelectDate
        dateFormatted={dateFormatted}
        currentDateZoned={format(
          addDays(new Date(currentDate), 1),
          'yyyy-MM-dd',
        )}
        handleNextDay={handleNextDay}
        handleDateChange={handleDateChange}
        handlePrevDay={handlePrevDay}
        disabledNextDay={
          new Date(currentDate).setHours(0, 0, 0) ===
          new Date().setHours(0, 0, 0)
        }
      />
      <Header>
        {profile && (
          <>
            {(profile.role === Role.ADMIN ||
              profile.role === Role.ENG ||
              profile.role === Role.EXIB ||
              profile.role === Role.OPEC) && (
              <>
                <Widget
                  title="Comerciais gravados"
                  handleClick={() => navigate('/dashboard/comerciaisgravados')}
                  icon={<FiVideo />}
                  color="success"
                  loading={loadingWidget}
                  type="condensed"
                  data={[
                    widgets.comerciaisGravados,
                    widgets.comerciaisGravados + widgets.comerciaisNaoGravados,
                    widgets.comerciaisGravadosArquivados,
                  ]}
                />
                <Widget
                  title="Comerciais não gravados"
                  handleClick={() =>
                    navigate('/dashboard/comerciaisnaogravados')
                  }
                  icon={<FiVideoOff />}
                  type="condensed"
                  color="danger"
                  loading={loadingWidget}
                  data={[
                    widgets.comerciaisNaoGravados,
                    null,
                    widgets.comerciaisNaoGravadosArquivados,
                  ]}
                />
              </>
            )}
            {(profile.role === Role.ADMIN || profile.role === Role.OPEC) && (
              <Widget
                title="Comerciais sem planilha"
                handleClick={() => navigate('/dashboard/comerciaissemplanilha')}
                type="condensed"
                icon={<FiBookmark />}
                color="info"
                loading={loadingWidget}
                data={widgets.semPlanilha}
              />
            )}
            {(profile.role === Role.ADMIN ||
              profile.role === Role.ENG ||
              profile.role === Role.EXIB) && (
              <>
                <Widget
                  title="Mídias Avulsas"
                  handleClick={() => navigate('/dashboard/midiasavulsas')}
                  type="condensed"
                  icon={<FiFilm />}
                  color="warn"
                  loading={loadingWidget}
                  data={[
                    widgets.midiasAvulsasGravadas,
                    widgets.midiasAvulsas,
                    null,
                  ]}
                />
                <Widget
                  title="Chamadas aprovadas"
                  handleClick={() => navigate('/dashboard/chamadasaprovadas')}
                  icon={<FiCheckCircle />}
                  color="success"
                  loading={loadingWidget}
                  type="condensed"
                  data={widgets.chamadasAprovadas}
                />
                <Widget
                  title="Chamadas válidas"
                  handleClick={() => navigate('/dashboard/chamadasvalidas')}
                  icon={<FiTv />}
                  color="info"
                  loading={loadingWidget}
                  type="condensed"
                  data={widgets.chamadasValidas}
                />
              </>
            )}
            {(profile.role === Role.ADMIN ||
              profile.role === Role.ENG ||
              profile.role === Role.OPEC) && (
              <Widget
                title="Comerciais reprovados"
                handleClick={() => navigate('/dashboard/comerciaisreprovados')}
                icon={<FiThumbsDown />}
                color="danger"
                loading={loadingWidget}
                type="condensed"
                data={widgets.comerciaisReprovados}
              />
            )}
          </>
        )}
      </Header>
      <Content>
        <LeftContent>
          <Card className="filadownloads">
            <header>
              <div>
                <p>Fila de download </p>
                <Button
                  id="filadownload_button"
                  fill
                  color="font"
                  round
                  onClick={handleGoToComerciaisIndisponiveis}
                  disabled={
                    profile &&
                    profile.role !== Role.ADMIN &&
                    profile.role !== Role.OPEC
                  }
                >
                  <FiInfo />
                </Button>
              </div>
              <div>
                <Form onSubmit={() => {}}>
                  <Input
                    name="searchLocal"
                    placeholder="Buscar na Tabela"
                    icon={<FiSearch />}
                    onChange={e => setFiltro(e.target.value)}
                  />
                </Form>
              </div>
            </header>
            {loadingWidget && <FilaDownloadLoader />}
            {!loadingWidget && filaDownloads && filaDownloads.length > 0 && (
              <>
                <input
                  type="file"
                  ref={inputFileRef}
                  style={{ display: 'none' }}
                  onChange={e => processUpload(e)}
                />
                <Scroll>
                  <Table>
                    <thead>
                      <tr>
                        <th />
                        <th>CM</th>
                        <th>TITULO</th>
                        <th>STATUS</th>
                        <th>PRIORIDADE</th>
                        <th>AÇÕES</th>
                      </tr>
                    </thead>

                    <tbody>
                      {!!filaDownloads &&
                        filaDownloads.map((fila, index) => (
                          <tr key={index}>
                            <TdFlag $isGe={fila.globoexpress}>
                              <div id={`tdFlag_id${index}`} />
                              <Tooltip
                                target={`tdFlag_id${index}`}
                                position="right"
                              >
                                <p>
                                  <strong>PRIORITÁRIO:</strong> GLOBO EXPRESS
                                </p>
                              </Tooltip>
                            </TdFlag>
                            <td>{fila.codmaterial}</td>
                            <td id={`tdTitulo_id${index}`}>{fila.titulo}</td>
                            <Tooltip
                              target={`tdTitulo_id${index}`}
                              position="bottom"
                            >
                              <p>
                                <strong>DESTINOS:</strong>
                                {fila.enviaexibidor
                                  .filter(env => env.emissora)
                                  .map(
                                    e =>
                                      `${e.emissora.sigla} (${format(
                                        new Date(e.allowed_at),
                                        'dd/MM',
                                      )})`,
                                  )
                                  .join(', ')}
                              </p>
                            </Tooltip>
                            <td>
                              {fila.uploading ? (
                                <ProgressBar
                                  completed={uploadProgress}
                                  bgColor={themeContext?.button.primary.out}
                                />
                              ) : (
                                <Flag
                                  name={fila.statusdownload}
                                  type={fila.statusdownload}
                                />
                              )}
                            </td>
                            <td>
                              <Switch
                                id={fila.id}
                                loading={fila.loading || false}
                                onChange={e => handleSwitchChange(e, fila)}
                                checked={fila.globoexpress}
                                disabled={false}
                              />
                            </td>
                            <td>
                              <div>
                                <DropDownMenu
                                  disabled={false}
                                  id={`acoes${index}`}
                                  actions={[
                                    {
                                      value: 1,
                                      label: 'REINICIAR DOWNLOAD',
                                      disabled:
                                        fila.statusdownload === 'indisponivel',
                                      icon: <FiRefreshCcw />,
                                    },
                                    {
                                      value: 2,
                                      label: 'UPLOAD',
                                      icon: <FiUploadCloud />,
                                      disabled:
                                        uploadProgress !== 0 ||
                                        (profile.role !== Role.ADMIN &&
                                          !fila.enviaexibidor
                                            .filter(env => env.emissora)
                                            .map(e => e.emissora.id)
                                            .some(fila_emissora =>
                                              profile.emissoras
                                                ?.map((e: Emissora) => e.id)
                                                .includes(fila_emissora),
                                            )),
                                    },
                                    {
                                      value: 0,
                                      label: 'HISTÓRICO',
                                      icon: <FiActivity />,
                                      href: `/historico/${fila.id}`,
                                    },
                                  ]}
                                  handleClick={action =>
                                    !action.disabled &&
                                    handleActionSelect(fila, action)
                                  }
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </Scroll>
              </>
            )}
            {!loadingWidget && filaDownloads && filaDownloads.length === 0 && (
              <NotFound title="" img={Download}>
                Nenhum download pendente
              </NotFound>
            )}
          </Card>
        </LeftContent>
        <RightContent>
          <Card>
            <header>
              <p>Planilha diária</p>
            </header>

            <PieChartContent>
              {loadingWidget && <PieChartLoader />}
              {!loadingWidget &&
                planilhaDiaria &&
                planilhaDiaria.data.datasets.length > 0 && (
                  <Pie
                    data={planilhaDiaria.data}
                    options={planilhaDiaria.options}
                  />
                )}
              {!loadingWidget &&
                planilhaDiaria &&
                planilhaDiaria.data.datasets.length === 0 && (
                  <MessageBox>
                    Planilha de gravação não disponibilizada pela OPEC
                  </MessageBox>
                )}
            </PieChartContent>
          </Card>

          <Card className="graphSemanal">
            <header>
              <p>Gráfico Semanal</p>
            </header>
            <ChartContent>
              {!loadingWidget && graphSemanal && (
                <Bar
                  data={graphSemanal.data}
                  options={graphSemanal.options}
                  getElementAtEvent={elements => {
                    handleBarGraphClick(elements);
                  }}
                />
              )}
            </ChartContent>
          </Card>
        </RightContent>
      </Content>
    </Container>
  );
}
